import { useState, useCallback, useEffect, useMemo } from 'react';

/**
 * Hook reutilizable para manejar filtros complejos de Dashboards.
 * 
 * @param {Object} initialFilters - Configuración inicial de filtros con value y matchMode.
 * @param {Function} fetcher - Función que llama a la API. Si se pasa, el hook maneja la carga.
 * @param {Number} debounceDelay - Tiempo de espera antes de llamar a la API tras un cambio (útil para inputs de texto/números).
 */
export const useFilters = (initialFilters, fetcher = null, debounceDelay = 500, isReady = true) => {
    const [valoresOriginales] = useState(initialFilters);
    const [filtros, setFiltros] = useState(initialFilters);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Cambia un solo filtro (Ej. País, Riesgo, Estado)
    const cambiarFiltro = useCallback((campo, valor) => {
        setFiltros((prev) => ({
            ...prev,
            [campo]: { ...prev[campo], value: valor }
        }));
    }, []);

    // Cambiar varios filtros a la vez (ej: rangos de fechas o montos)
    const cambiarMultiplesFiltros = useCallback((nuevosValores) => {
        setFiltros((prev) => {
            const actualizados = { ...prev };
            Object.keys(nuevosValores).forEach((key) => {
                if (actualizados[key]) {
                    actualizados[key] = { ...actualizados[key], value: nuevosValores[key] };
                }
            });
            return actualizados;
        });
    }, []);

    // Limpia y genera el payload exacto para el back en Laravel
    const obtenerPayloadBackend = () => {
        const payload = {};
        Object.keys(filtros).forEach((key) => {
            const { value, matchMode } = filtros[key];
            const hasValue = value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);
            if (hasValue) {
                let cleanValue = value;

                // Si es un Array (Múltiples chips)
                if (Array.isArray(value)) {
                    cleanValue = value.map(item => {
                        // Si el item es un objeto que tiene la propiedad "value" (como los del Autocomplete), extraemos solo el value.
                        // Si es un primitivo (ej: "MUY_BAJO_CLIENTE" o 3530), lo dejamos tal cual.
                        if (item !== null && typeof item === 'object' && item.value !== undefined) {
                            return item.value;
                        }
                        return item;
                    });
                }
                // Si es un Autocomplete de selección única (Un solo objeto)
                else if (value !== null && typeof value === 'object' && value.value !== undefined && !(value instanceof Date)) {
                    cleanValue = value.value;
                }
                payload[key] = { value: cleanValue, matchMode };
            }
        });
        return payload;
    };

    // Efecto de Auto-Fetch con Debounce (Anti-rebote)
    useEffect(() => {
        if (!fetcher || !isReady) return;
        setLoading(true);
        const timer = setTimeout(() => {
            const payload = obtenerPayloadBackend();
            fetcher(payload).then((result) => {
                setData(result);
            }).catch((error) => {
                throw error;
            }).finally(() => {
                setLoading(false);
            });
        }, debounceDelay);
        // Si los filtros cambian antes del delay, cancelamos la petición anterior
        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [filtros, isReady]);

    // Helper manual por si quieres forzar recargas desde un botón "Actualizar"
    const recargar = () => {
        setFiltros(prev => ({ ...prev }));
    };

    const totalFiltrosActivos = useMemo(() => {
        let contador = 0;

        Object.keys(filtros).forEach((key) => {
            const val = filtros[key].value;
            const originalVal = valoresOriginales[key]?.value; // <-- Tomamos el valor congelado del día 1

            // 1. Si es un arreglo, sumamos 1 si tiene elementos seleccionados
            if (Array.isArray(val)) {
                if (val.length > 0) contador++;
            }
            // 2. Si es cualquier otra cosa (fechas, strings, números) y tiene contenido
            else if (val !== null && val !== undefined && val !== '') {

                // Si es un objeto Date (como tus fechas) comparamos sus milisegundos
                if (val instanceof Date && originalVal instanceof Date) {
                    // Solo lo contamos si el usuario eligió una fecha diferente a la predeterminada
                    if (val.getTime() !== originalVal.getTime()) {
                        contador++;
                    }
                }
                // Si es un número o texto predeterminado, validamos que el usuario lo haya cambiado
                else if (val !== originalVal) {
                    contador++;
                }
            }
        });

        return contador;
    }, [filtros, valoresOriginales]); // <-- Dependencias correctas

    const resetearFiltros = useCallback(() => {
        setFiltros(valoresOriginales);
    }, [valoresOriginales]);

    return {
        filtros,
        cambiarFiltro,
        cambiarMultiplesFiltros,
        obtenerPayloadBackend,
        data,
        loading,
        recargar,
        totalFiltrosActivos,
        resetearFiltros
    };
};